import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../utils/gameUtils';

export const usePvPGame = (userId) => {
    const [socket, setSocket] = useState(null);
    const [gameState, setGameState] = useState('initial'); // initial, matching, playing, finished
    const [topic, setTopic] = useState('');
    const [myScore, setMyScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);
    const [history, setHistory] = useState([]); // List of my valid words

    const [messages, setMessages] = useState([]); // List of {id, text}
    const [result, setResult] = useState(null); // { winner: 'id', my_score, opp_score }
    const [timeLeft, setTimeLeft] = useState(30);

    const timerRef = useRef(null);
    const msgIdRef = useRef(0);

    const addMessage = (text) => {
        const id = msgIdRef.current++;
        setMessages(prev => [...prev, { id, text }]);
        setTimeout(() => {
            setMessages(prev => prev.filter(m => m.id !== id));
        }, 3000);
    };

    const intentionalDisconnect = useRef(false);

    // Initialize WebSocket
    const startMatching = () => {
        if (socket) return;

        intentionalDisconnect.current = false;
        setGameState('matching');
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        let wsUrl = API_URL.replace(/^http/, 'ws');
        if (!wsUrl) {
            // Default to current host, but if localhost:3000, assume backend is 8000
            if (window.location.hostname === 'localhost' && window.location.port === '3000') {
                wsUrl = `${protocol}//localhost:8000`;
            } else {
                wsUrl = `${protocol}//${window.location.host}`;
            }
        }
        // Remove trailing slash if exists
        wsUrl = wsUrl.replace(/\/$/, "");

        // Use a random session ID appended to userId to allow self-matching in different tabs
        const sessionSuffix = Math.floor(Math.random() * 10000);
        const connectionId = `${userId}_${sessionSuffix}`;

        const ws = new WebSocket(`${wsUrl}/pvp/ws/${connectionId}`);

        ws.onopen = () => {
            console.log('Connected to PvP Server');
            addMessage('サーバーに接続しました。対戦相手を探しています...');
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleServerMessage(data);
        };

        ws.onclose = () => {
            console.log('Disconnected from PvP Server');
            if (!intentionalDisconnect.current) {
                addMessage('サーバーから切断されました');
                setGameState('finished');
            }
        };

        setSocket(ws);
    };

    const handleServerMessage = (data) => {
        switch (data.type) {
            case 'info':
                addMessage(data.message);
                break;
            case 'ready':
                setTopic(data.topic);
                setGameState('countdown');
                setMyScore(0);
                setOpponentScore(0);
                setHistory([]);
                setResult(null);
                break;
            case 'start':
                setGameState('playing');
                addMessage(`対戦開始！お題:「${data.topic}」`);
                startTime();
                break;
            case 'result':
                // My submission result
                if (data.valid) {
                    setMyScore(data.score);
                    setHistory(prev => [data.word, ...prev]);
                } else {
                    addMessage(`無効: ${data.reason}`);
                }
                break;
            case 'opponent_score':
                setOpponentScore(data.score);
                break;
            case 'finish':
                setResult(data);
                setGameState('finished');
                if (timerRef.current) clearInterval(timerRef.current);
                break;
            default:
                break;
        }
    };

    const startTime = () => {
        setTimeLeft(30);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    // Optionally send time_up to server if server doesn't manage time
                    // But server should ideally manage.
                    // For this implementation, we wait for server or just stop locally?
                    // Plan says 30s fixed.
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const submitWord = (word) => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: "word_submission", word: word }));
        }
    };

    const disconnect = () => {
        intentionalDisconnect.current = true;
        if (socket) {
            socket.close();
            setSocket(null);
        }
        if (timerRef.current) clearInterval(timerRef.current);
        setGameState('initial');
        setMyScore(0);
        setOpponentScore(0);
        setHistory([]);
        setMessages([]);
        setTopic('');
        setResult(null); // Clear result too
    };

    useEffect(() => {
        return () => {
            if (socket) socket.close();
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []); // eslint-disable-line

    return {
        gameState,
        topic,
        myScore,
        opponentScore,
        history,
        messages,
        result,
        timeLeft,
        startMatching,
        submitWord,
        disconnect
    };
};
